import React, { useEffect, useState, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Typography,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  CircularProgress,
  Link,
  Modal,
  Container
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import LanguageIcon from '@mui/icons-material/Language';
import html2canvas from 'html2canvas';
import { GENERATE_NAME_ENDPOINT } from '../config/api';

const languages = [
  { code: 'en', name: 'English' },
  { code: 'es', name: 'Español' },
  { code: 'fr', name: 'Français' },
  { code: 'de', name: 'Deutsch' },
  { code: 'ru', name: 'Русский' },
  { code: 'ar', name: 'العربية' },
  { code: 'ko', name: '한국어' },
  { code: 'ja', name: '日本語' },
  { code: 'zh', name: '中文' }
];

const modalStyle = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: '90%',
  maxWidth: 400,
  borderRadius: 2,
  p: 0,
  outline: 'none',
  bgcolor: 'background.paper'
};

const cardStyle = {
  p: 4,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  borderRadius: 2,
  position: 'relative',
  textAlign: 'center',
  backgroundImage: `url("data:image/svg+xml,%3Csvg width='100' height='100' viewBox='0 0 100 100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M11 18c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm48 25c3.866 0 7-3.134 7-7s-3.134-7-7-7-7 3.134-7 7 3.134 7 7 7zm-43-7c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm63 31c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM34 90c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zm56-76c1.657 0 3-1.343 3-3s-1.343-3-3-3-3 1.343-3 3 1.343 3 3 3zM12 86c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm28-65c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm23-11c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-6 60c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm29 22c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zM32 63c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm57-13c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm-9-21c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM60 91c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM35 41c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2zM12 60c1.105 0 2-.895 2-2s-.895-2-2-2-2 .895-2 2 .895 2 2 2z' fill='%23ff2442' fill-opacity='0.03' fill-rule='evenodd'/%3E%3C/svg%3E")`,
  backgroundAttachment: 'fixed'
};

const scoreStyle = {
  position: 'absolute',
  top: 16,
  right: 16,
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  border: '2px solid #FF2442',
  borderRadius: 2,
  padding: '8px 16px'
};

const meaningStyle = {
  mt: 3,
  textAlign: 'left',
  whiteSpace: 'pre-wrap'
};

const explanationStyle = {
  mt: 4,
  textAlign: 'left',
  whiteSpace: 'pre-wrap'
};

const downloadButtonStyle = {
  mt: 2,
  py: 1.5,
  backgroundColor: '#FF2442',
  color: 'white',
  borderRadius: '50px',
  '&:hover': {
    backgroundColor: '#ff4d66'
  },
  boxShadow: '0 2px 8px rgba(255, 36, 66, 0.25)',
  fontWeight: 'bold'
};

const ImagePreviewModal = ({ open, onClose, nameData }) => {
  const cardRef = useRef(null);
  const { t } = useTranslation();

  const handleDownload = async () => {
    if (cardRef.current) {
      try {
        // 创建canvas
        const canvas = await html2canvas(cardRef.current, {
          backgroundColor: '#ffffff',
          scale: 2, // 提高图片质量
        });

        // 转换为图片并下载
        const image = canvas.toDataURL('image/png', 1.0);
        const link = document.createElement('a');
        link.download = `${nameData.chineseName}-名字解释.png`;
        link.href = image;
        link.click();
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
  };

  return (
    <Modal 
      open={open} 
      onClose={onClose}
      aria-labelledby="image-preview-modal"
    >
      <Box sx={modalStyle}>
        <Box sx={{ 
          maxHeight: 'calc(90vh - 80px)',
          overflow: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '4px'
          }
        }}>
          <Box ref={cardRef} sx={cardStyle}>
            <Typography 
              variant="h4" 
              component="h2"
              sx={{ 
                fontWeight: 'bold',
                color: '#FF2442',
                mb: 1
              }}
            >
              {nameData.chineseName}
            </Typography>
            <Typography 
              variant="h6"
              sx={{ 
                color: 'text.secondary',
                mb: 1
              }}
            >
              ({nameData.pinyin})
            </Typography>

            {nameData.score && (
              <Paper
                elevation={0}
                sx={scoreStyle}
              >
                <Typography 
                  variant="h4" 
                  color="#FF2442" 
                  sx={{ 
                    fontWeight: 'bold',
                    lineHeight: 1,
                    mb: 0.3
                  }}
                >
                  {nameData.score}
                </Typography>
                <Typography 
                  variant="caption" 
                  color="#FF2442"
                  sx={{ 
                    opacity: 0.8,
                    fontWeight: 'normal'
                  }}
                >
                  分
                </Typography>
              </Paper>
            )}

            {nameData.meaning && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#FFF5F7',
                  borderRadius: 2,
                  whiteSpace: 'pre-wrap'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', lineHeight: 1.8 }}>
                  {nameData.meaning}
                </Typography>
              </Box>
            )}

            {nameData.explanation && (
              <Box
                sx={{
                  mt: 4,
                  whiteSpace: 'pre-wrap'
                }}
              >
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  {nameData.explanation}
                </Typography>
              </Box>
            )}
          </Box>
        </Box>

        <Box sx={{ 
          p: 2,
          borderTop: 1,
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Button
            fullWidth
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            sx={downloadButtonStyle}
            disableElevation
          >
            {t('downloadImage')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

function ResultPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [nameData, setNameData] = useState(location.state || {});
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [generationState, setGenerationState] = useState({
    status: 'idle',
    progress: 0,
    currentContent: '',
    result: null
  });
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  useEffect(() => {
    if (!location.state || !location.state.chineseName) {
      navigate('/');
      return;
    }

    // 设置初始数据
    setNameData(location.state);

    // 监听名字数据更新事件
    const handleDataUpdate = (event) => {
      console.log('Name data update:', event.detail);
      setNameData(prevData => ({
        ...prevData,
        ...event.detail
      }));
    };

    window.addEventListener('nameDataUpdate', handleDataUpdate);

    return () => {
      window.removeEventListener('nameDataUpdate', handleDataUpdate);
    };
  }, [location.state, navigate]);

  if (!location.state || !location.state.chineseName) {
    return null;
  }

  const { chineseName, pinyin, meaning, originalName, score, explanation } = nameData;

  const handleBack = () => {
    navigate('/');
  };

  const handleLanguageClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLanguageClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageSelect = (langCode) => {
    i18n.changeLanguage(langCode);
    handleLanguageClose();
  };

  const handleRegenerate = async () => {
    setIsLoading(true);
    setGenerationState({
      status: 'generating',
      progress: 0,
      currentContent: '',
      result: null
    });
    setError(null);

    try {
      const response = await fetch(GENERATE_NAME_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          name: originalName || '',
          language: i18n.language,
          excludeName: chineseName
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader.read();
        
        if (done) break;

        const chunk = decoder.decode(value);
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              
              if (data.error) {
                throw new Error(data.error);
              }
              
              setGenerationState(prevState => ({
                ...prevState,
                status: data.status,
                progress: data.progress,
                currentContent: data.current_content,
                result: data.result
              }));

              if (data.result) {
                setNameData(prevData => ({
                  ...prevData,
                  ...data.result
                }));
              }
            } catch (e) {
              if (!(e instanceof SyntaxError)) {
                throw e;
              }
            }
          }
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = () => {
    setImagePreviewOpen(true);
  };

  const handleImagePreviewClose = () => {
    setImagePreviewOpen(false);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      position: 'relative',
      pb: 8 
    }}>
      <Container maxWidth="sm" sx={{ pt: 3, px: 2 }}>
        <Box sx={{ 
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          pt: 4,
          pb: 4
        }}>
          <IconButton 
            onClick={handleBack}
            sx={{ 
              position: 'absolute',
              left: 16,
              top: 16
            }}
          >
            <ArrowBackIcon />
          </IconButton>

          <IconButton
            onClick={handleLanguageClick}
            sx={{ 
              position: 'absolute',
              right: 16,
              top: 16
            }}
          >
            <LanguageIcon />
          </IconButton>

          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleLanguageClose}
            onClick={handleLanguageClose}
          >
            {languages.map((lang) => (
              <MenuItem
                key={lang.code}
                onClick={() => handleLanguageSelect(lang.code)}
                selected={i18n.language === lang.code}
              >
                {lang.name}
              </MenuItem>
            ))}
          </Menu>

          <Typography 
            variant="h5" 
            component="h1" 
            align="center"
            sx={{ 
              mb: 4,
              fontWeight: 'bold',
              color: '#000000'
            }}
          >
            {t('yourChineseName')}
          </Typography>

          <Paper 
            elevation={3}
            sx={{
              p: 4,
              mb: 4,
              borderRadius: 2,
              backgroundColor: '#fff'
            }}
          >
            <Box sx={{ position: 'relative', mb: 4 }}>
              <Box sx={{ textAlign: 'center' }}>
                <Typography 
                  variant="h4" 
                  component="h1" 
                  sx={{ 
                    fontWeight: 'bold',
                    color: '#FF2442',
                    mb: 1
                  }}
                >
                  {chineseName}
                </Typography>
                <Typography 
                  variant="h6"
                  sx={{ 
                    color: 'text.secondary',
                    lineHeight: 1,
                    mb: 1
                  }}
                >
                  ({pinyin})
                </Typography>
              </Box>

              {score !== null && (
                <Paper
                  elevation={0}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    display: 'inline-flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    border: '2px solid #FF2442',
                    px: 2.5,
                    py: 1.2,
                    borderRadius: 3,
                    bgcolor: 'transparent'
                  }}
                >
                  <Typography 
                    variant="h4" 
                    color="#FF2442" 
                    sx={{ 
                      fontWeight: 'bold',
                      lineHeight: 1,
                      mb: 0.3
                    }}
                  >
                    {score}
                  </Typography>
                  <Typography 
                    variant="caption" 
                    color="#FF2442"
                    sx={{ 
                      opacity: 0.8,
                      fontWeight: 'normal'
                    }}
                  >
                    {t('score')}
                  </Typography>
                </Paper>
              )}
            </Box>

            {meaning && (
              <Box
                sx={{
                  mt: 3,
                  p: 2,
                  backgroundColor: '#FFF5F7',
                  borderRadius: 2,
                  whiteSpace: 'pre-wrap'
                }}
              >
                <Typography variant="body1" sx={{ fontWeight: 'bold', lineHeight: 1.8 }}>
                  {meaning}
                </Typography>
              </Box>
            )}

            {explanation && (
              <Box
                sx={{
                  mt: 4,
                  whiteSpace: 'pre-wrap'
                }}
              >
                <Typography variant="body1" sx={{ lineHeight: 1.8 }}>
                  {explanation}
                </Typography>
              </Box>
            )}
          </Paper>
        </Box>
      </Container>

      <Box sx={{ 
        position: 'fixed', 
        bottom: '60px', 
        left: 0, 
        right: 0, 
        py: 1.5,
        px: 2,
        display: 'flex',
        justifyContent: 'center',
        gap: 2,
        zIndex: 1000
      }}>
        <Button
          startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
          onClick={handleRegenerate}
          disabled={isLoading}
          sx={{
            minWidth: '160px',
            backgroundColor: '#FFF5F7',
            color: '#FF2442',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: '16px',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#FFE4E8',
            },
            '&:disabled': {
              backgroundColor: '#FFF5F7',
              color: 'rgba(255, 36, 66, 0.5)',
            }
          }}
        >
          {t('regenerateName')}
        </Button>
        <Button
          startIcon={<DownloadIcon />}
          onClick={handleDownload}
          sx={{
            minWidth: '160px',
            backgroundColor: '#FF2442',
            color: '#FFFFFF',
            fontWeight: 600,
            fontSize: '0.875rem',
            borderRadius: '16px',
            padding: '8px 16px',
            '&:hover': {
              backgroundColor: '#E61E3C',
            }
          }}
        >
          {t('generateCard')}
        </Button>
      </Box>

      <Box sx={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        width: '100%',
        bgcolor: '#FFF',
        py: 1.5,
        textAlign: 'center',
        borderTop: '1px solid #FFE4E8',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        zIndex: 999
      }}>
        <Typography variant="body2" sx={{ 
          color: 'text.secondary',
          display: 'flex',
          alignItems: 'center',
          gap: 0.5
        }}>
          {t('contact')}
        </Typography>
        <Button
          size="small"
          startIcon={<span role="img" aria-label="sweet potato">🍠</span>}
          sx={{
            color: '#FF2442',
            fontSize: '0.875rem',
            fontWeight: 600,
            backgroundColor: '#FFF5F7',
            borderRadius: '16px',
            padding: '4px 12px',
            minWidth: 'auto',
            '&:hover': {
              backgroundColor: '#FFE4E8',
            }
          }}
          onClick={() => window.open('https://www.xiaohongshu.com/user/profile/566a444ccb35fb35e85b129a?xsec_token=YBXbFDTrdsLoLLIcwMq6Z7kv-DxH8-ErAriLOKhfTt2Hg%3D&xsec_source=app_share&xhsshare=CopyLink&appuid=566a444ccb35fb35e85b129a&apptime=1737155507&share_id=9b5c59fb1c034e4cb9500c2a4d3b36f2', '_blank')}
        >
          {t('xiaohongshu')}
        </Button>
      </Box>

      <ImagePreviewModal 
        open={imagePreviewOpen} 
        onClose={handleImagePreviewClose} 
        nameData={nameData} 
      />
    </Box>
  );
}

export default ResultPage;
